// Capa anticorrupción de Control MDM: traduce el wire del expediente (`loan-credit-business`) a los
// tipos de la pantalla. Lo que el silo no guarda queda AUSENTE — nunca se rellena con un invento.
import type { LoanDevicePinUnlockResult } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanDevicePinUnlockResult';
import type { LoanDeviceLockReasonEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceLockReasonEnum';
import type { LoanDeviceMdmStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceMdmStatusEnum';
import type { MdmDeviceStatusEnum } from '@fiado/type-kit/bin/mdm/enums/MdmDeviceStatusEnum';
import { MdmOperationStatusEnum } from '@fiado/type-kit/bin/mdm/enums/MdmOperationStatusEnum';
import type { MdmOperationTypeEnum } from '@fiado/type-kit/bin/mdm/enums/MdmOperationTypeEnum';
import type { MdmProviderEnum } from '@fiado/type-kit/bin/mdm/enums/MdmProviderEnum';
import type { MdmLockModeEnum } from '@fiado/type-kit/bin/retailCatalog/enums/MdmLockModeEnum';
import type {
    LoanDeviceExtendValidityResult,
    LoanDeviceFleetItem,
    LoanDeviceOperationItem,
    LoanDeviceRefreshReview,
} from '@/api/services/loan-credit/contract';
import type { components } from '@/api/services/loan-credit/loan-credit.gen';
import { wireEnum } from '@/lib/wireEnum';
import { mdmItemErrorMessage } from '../actionErrors';
import type { MdmAuditEntry, MdmFleetDevice, MdmRefreshReview } from '../types';

/**
 * Una fila del expediente, tal como la pinta la pantalla.
 *
 * `creditRef` cae al `creditId` porque el expediente MDM no guarda folio legible. Marca y modelo
 * los resuelve el lambda contra el catálogo, y el cliente sigue sin viajar en este endpoint.
 */
export function toFleetDevice(item: LoanDeviceFleetItem): MdmFleetDevice {
    return {
        creditId: item.creditId,
        imei: item.imei,
        mdmStatus: wireEnum<LoanDeviceMdmStatusEnum>(item.mdmStatus),
        mdmProvider: wireEnum<MdmProviderEnum>(item.mdmProvider),
        mdmLockMode: wireEnum<MdmLockModeEnum>(item.mdmLockMode),
        mdmConfirmed: item.mdmConfirmed,
        mdmConfirmedAt: item.mdmConfirmedAt,
        devicePasskeyLength: item.devicePasskeyLength,
        mdmAlerts: item.mdmAlerts,
        lockRefutedByProvider: item.lockRefutedByProvider,
        productSku: item.productSku,
        brand: item.brand,
        model: item.model,
        creditRef: item.creditId,
        lockReason:
            item.lockReason === undefined
                ? undefined
                : wireEnum<LoanDeviceLockReasonEnum>(item.lockReason),
        lockedAt: item.lockedAt,
        currentLockScheduledFor: item.currentLockScheduledFor,
        unlockedAt: item.unlockedAt,
        lastSeenAt: item.lastSeenAt,
        enrolledAt: item.enrolledAt,
        lastOperationType:
            item.lastOperationType === undefined
                ? undefined
                : wireEnum<MdmOperationTypeEnum>(item.lastOperationType),
        lastOperationAt: item.lastOperationAt,
        updatedAt: item.updatedAt,
        pendingOperations: item.pendingOperations,
        operationReference: item.operationReference,
        pollingHandle: item.pollingHandle,
        lastError: item.lastError,
    };
}

/**
 * El veredicto de una consulta al proveedor, tal como lo pinta la ficha.
 *
 * `reason` sólo viaja con `NO_EVIDENCE` y `providerErrorCode` sólo con `PROVIDER_SILENT`: ausentes
 * se leen como «no aplica», nunca como un valor por defecto.
 */
export function toRefreshReview(review: LoanDeviceRefreshReview): MdmRefreshReview {
    return {
        outcome: review.outcome,
        observedAt: review.observedAt,
        reason: review.reason,
        providerStatus:
            review.providerStatus === undefined
                ? undefined
                : wireEnum<MdmDeviceStatusEnum>(review.providerStatus),
        providerErrorCode: review.providerErrorCode,
        changed: review.changed,
    };
}

/**
 * Una llamada al proveedor, tal como la pinta la bitácora de la ficha.
 *
 * El `creditId` viaja aparte porque el log se consulta POR crédito y las filas no lo repiten.
 */
export function toAuditEntry(item: LoanDeviceOperationItem, creditId: string): MdmAuditEntry {
    return {
        callId: item.operationId,
        creditId,
        imei: item.imei,
        provider: wireEnum<MdmProviderEnum>(item.mdmProvider),
        operationType: wireEnum<MdmOperationTypeEnum>(item.operationType),
        status: wireEnum<MdmOperationStatusEnum>(item.status),
        errorCode: item.errorCode,
        errorMessage: item.errorMessage,
        at: item.occurredAt,
    };
}

/** Cómo terminó UN equipo del lote: el 200 trae el desenlace adentro, no en el status HTTP. */
export interface MdmActionItemOutcome {
    /** Ausente cuando el ítem salió bien o quedó en vuelo. */
    errorMessage?: string;
    /** El equipo quedó desbloqueado y SIN autolock programado: hay que escalarlo. */
    leftUnprotected: boolean;
    /** El proveedor aceptó la orden (`PENDING`) pero el equipo todavía no la confirma. */
    pending?: boolean;
    /** Hora del corte que instaló el bloqueo; ausente si el proveedor no programó ninguno. */
    effectiveAt?: string;
    /** Passcode de PIN-UNLOCK. De un solo uso: no se guarda ni se puede volver a pedir. */
    pin?: string;
    /** Vigencia del passcode que reportó el proveedor. */
    validitySeconds?: number;
}

/** La forma mínima que comparte cualquier resultado por ítem; `phase` solo la trae la extensión. */
type ActionItemResult = Partial<
    Pick<LoanDeviceExtendValidityResult, 'status' | 'error' | 'phase'> &
        Pick<components['schemas']['LoanDeviceLockResult'], 'currentLockScheduledFor'> &
        Pick<LoanDevicePinUnlockResult, 'pin' | 'validitySeconds'>
>;

/**
 * `SCHEDULE_FAILED` no es un error más: el desbloqueo SÍ salió y la reprogramación no, así que el
 * equipo quedó libre y sin corte. El lambda no lo vuelve a bloquear a propósito, y este mensaje es
 * lo primero que se lo dice al operador.
 */
const UNPROTECTED_DEVICE_MESSAGE =
    'El proveedor recibió el desbloqueo, pero la reprogramación del corte no se instaló: el equipo quedó sin bloqueo automático. Escálalo.';

/**
 * Lee el desenlace de un ítem del lote. Un `status: ERROR` contado como éxito le diría al operador
 * que bloqueó un equipo que sigue libre.
 */
export function readActionItemOutcome(value: unknown): MdmActionItemOutcome {
    const result = (value ?? {}) as ActionItemResult;
    // La fase se lee ANTES del status: `SCHEDULE_FAILED` también llega en PENDING, y ahí el equipo
    // ya quedó suelto aunque el lambda no declare la operación en error.
    if (result.phase === 'SCHEDULE_FAILED') {
        return { errorMessage: UNPROTECTED_DEVICE_MESSAGE, leftUnprotected: true };
    }
    if (result.status !== MdmOperationStatusEnum.ERROR) {
        // `lockedAt` es la hora de la orden; el corte que cae en el equipo es otro campo. El `pin`
        // llega solo en PIN-UNLOCK y no pasa por ningún store: es de un solo uso.
        return {
            leftUnprotected: false,
            pending: result.status === MdmOperationStatusEnum.PENDING,
            effectiveAt: result.currentLockScheduledFor,
            ...(result.pin ? { pin: result.pin } : {}),
            ...(result.validitySeconds ? { validitySeconds: result.validitySeconds } : {}),
        };
    }
    return { errorMessage: mdmItemErrorMessage(result.error), leftUnprotected: false };
}
